import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {EventServiceRegistrationResponse, UserResponse, UserEntityRelationWithUserDataResponse} from '../../../../../../../../../../../../@shared/models/response.module';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {EventsService} from '../../../../../../../../../../../../@shared/services/events-service';
import {ToastService} from '../../../../../../../../../../../../@core/services/toast.service';
import {_AQH_EVE_35} from '../../../../../../../../../../../../@shared/models/input.module';
import {CompanyService} from '../../../../../../../../../../../../@shared/services/company.service';

@Component({
  selector: 'app-edit-service-registration',
  standalone: false,
  templateUrl: './edit-service-registration.html',
  styleUrl: './edit-service-registration.css'
})
export class EditServiceRegistration implements OnInit {
  @Input() registration!: EventServiceRegistrationResponse;

  form: FormGroup;
  saving = false;
  loadingUsers = false;

  availableUsers: UserEntityRelationWithUserDataResponse[] = [];
  filteredUsers: UserEntityRelationWithUserDataResponse[] = [];

  selectedUser: UserResponse | null = null;

  userSearchTerm = '';

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private eventsService: EventsService,
    private toastService: ToastService,
    private companyService: CompanyService
  ) {
    this.form = this.fb.group({
      user_id: ['', Validators.required],
      num_registrations: [1, [Validators.required, Validators.min(1)]]
    });
  }

  ngOnInit() {
    if (this.registration) {
      this.selectedUser = this.registration.user || null;

      // Set initial search term
      if (this.selectedUser) {
        this.userSearchTerm = `${this.selectedUser.first_name} ${this.selectedUser.last_name}`;
      }

      // Set initial form values
      this.form.patchValue({
        user_id: this.registration.user_id,
        num_registrations: this.registration.num_registrations || 1
      });
    }

    // Load all users
    this.loadUsers();
  }

  loadUsers() {
    this.loadingUsers = true;
    this.companyService.getCompanyEmployees().subscribe({
      next: (users: UserEntityRelationWithUserDataResponse[]) => {
        this.availableUsers = users || [];
        this.filteredUsers = this.availableUsers;
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
        this.availableUsers = [];
        this.filteredUsers = [];
        this.toastService.error('Failed to load users');
      }
    });
  }

  onUserSearch() {
    const searchTerm = this.userSearchTerm.toLowerCase().trim();

    if (!searchTerm) {
      this.filteredUsers = this.availableUsers;
      return;
    }

    this.filteredUsers = this.availableUsers.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email?.toLowerCase() || '';

      return fullName.includes(searchTerm) || email.includes(searchTerm);
    });
  }

  selectUser(user: UserEntityRelationWithUserDataResponse) {
    this.selectedUser = user as any;
    this.form.patchValue({ user_id: user.user_id });
    this.userSearchTerm = `${user.first_name} ${user.last_name}`;
    this.filteredUsers = [];
  }

  clearUserSearch() {
    this.userSearchTerm = '';
    this.selectedUser = null;
    this.form.patchValue({ user_id: '' });
    this.filteredUsers = [];
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload: _AQH_EVE_35 = {
      event_service_registration_id: this.registration.event_service_registration_id,
      user_id: this.form.get('user_id')!.value,
      num_registrations: this.form.get('num_registrations')!.value
    };

    this.eventsService.updateEventServiceRegistration(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success('Registration updated successfully');
        this.activeModal.close(true);
      },
      error: () => {
        this.saving = false;
        this.toastService.error('Failed to update registration');
      }
    });
  }

  close() {
    this.activeModal.close();
  }

  get userIdInvalid(): boolean {
    const c = this.form.get('user_id');
    return !!(c && c.invalid && c.touched);
  }
}
